import React, { FC, useEffect, useState } from 'react';

import { Modal } from '../../../components/modal/index';
import { TeamMember, Vacation } from '../../../types/team.types';
import { getFieldErrors, vacationFormSchema, VacationFormValues } from '../team.validation';
import { DateField, SelectField, TextAreaField } from '../team-field';

export type VacationFormModalProps = {
  defaultMemberId?: string;
  isOpen: boolean;
  isPending: boolean;
  members: TeamMember[];
  onDelete?: () => void;
  onDismiss: () => void;
  onSubmit: (values: VacationFormValues) => void;
  vacation: null | Vacation;
};

const buildEmptyValues = (defaultMemberId = ''): VacationFormValues => ({
  teamMemberId: defaultMemberId,
  startDate: '',
  endDate: '',
  notes: '',
});

export const VacationFormModal: FC<VacationFormModalProps> = ({
  isOpen,
  isPending,
  members,
  vacation,
  defaultMemberId,
  onDelete,
  onDismiss,
  onSubmit,
}) => {
  const [values, setValues] = useState<VacationFormValues>(buildEmptyValues());
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!isOpen) return;

    setErrors({});
    setValues(
      vacation
        ? {
            teamMemberId: vacation.teamMemberId,
            startDate: vacation.startDate,
            endDate: vacation.endDate,
            notes: vacation.notes ?? '',
          }
        : buildEmptyValues(defaultMemberId),
    );
  }, [isOpen, vacation, defaultMemberId]);

  const setField = (field: keyof VacationFormValues) => (value: string) =>
    setValues((prev) => ({ ...prev, [field]: value }));

  const handleSave = () => {
    const result = vacationFormSchema.safeParse(values);

    if (!result.success) {
      setErrors(getFieldErrors(result.error));
      return;
    }

    onSubmit(result.data);
  };

  const memberOptions = [
    { label: 'Select a team member', value: '' },
    ...members.map((member) => ({ label: member.name, value: member.id })),
  ];

  return (
    <Modal
      className="max-w-[480px]"
      crossButton
      dismissButtonContent="Cancel"
      isOpen={isOpen}
      onDismiss={onDismiss}
      onHardClose={onDismiss}
      onSuccess={isPending ? undefined : handleSave}
      successButtonClassName="bg-brand-700"
      successButtonContent={isPending ? 'Saving…' : vacation ? 'Save changes' : 'Add vacation'}
      title={vacation ? 'Edit vacation' : 'Add vacation'}
    >
      <div className="flex flex-col gap-4">
        <SelectField
          error={errors.teamMemberId}
          label="Who's off?"
          onChange={setField('teamMemberId')}
          options={memberOptions}
          value={values.teamMemberId}
        />
        <div className="flex gap-3">
          <DateField
            error={errors.startDate}
            label="Start date"
            onChange={setField('startDate')}
            value={values.startDate}
          />
          <DateField
            error={errors.endDate}
            label="End date"
            min={values.startDate || undefined}
            onChange={setField('endDate')}
            value={values.endDate}
          />
        </div>
        <TextAreaField
          error={errors.notes}
          label="Notes (optional)"
          onChange={setField('notes')}
          placeholder="e.g. Family trip"
          value={values.notes}
        />
        {vacation && onDelete && (
          <button
            className="self-start text-xs font-[550] text-fireside-600 hover:underline"
            onClick={onDelete}
            type="button"
          >
            Delete this vacation
          </button>
        )}
      </div>
    </Modal>
  );
};
